// @pages/account/PaymentSetup.page.tsx
import React, { Suspense, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ok } from "@ps-bridge/ok";
import { buildPaymentSetupViewProps as _buildPaymentSetupViewProps } from "@ps-bridge/paymentSetup.program";
import EmptyView from "@view/EmptyView";
import PaymentSetupMissingSecretView from "@view/payment/PaymentSetupMissingSecret.view";
import { usePaymentProvider } from "@contexts/PaymentClient.context";
import * as Option from "@utils/Option";
import { getParamOption } from "@utils/searchParams";
import PaymentSetupPanel, {
    type PaymentSetupPhase,
} from "@view/panels/payment/PaymentSetup.panel";

const StripePaymentSetup = React.lazy(() => import("./StripePaymentSetup.page"));
const SquarePaymentSetup = React.lazy(() => import("./SquarePaymentSetup.page"));

const PaymentSetupPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const paymentProvider = usePaymentProvider();
    const setupIntentSecret = useMemo(
        () => Option.fold(getParamOption(searchParams, "setup_intent_secret"), () => "", (value) => value),
        [searchParams],
    );

    const hasSetupIntentSecret = setupIntentSecret.trim().length > 0;
    const phase: PaymentSetupPhase =
        paymentProvider === "square"
            ? "square"
            : hasSetupIntentSecret
                ? "stripe"
                : "missingSecret";

    const squareView = (
        <Suspense fallback={<EmptyView />}>
            <SquarePaymentSetup />
        </Suspense>
    );
    const stripeView = (
        <Suspense fallback={<EmptyView />}>
            <StripePaymentSetup setupIntentSecret={setupIntentSecret} />
        </Suspense>
    );
    const missingSecretView = (
        <PaymentSetupMissingSecretView
            onBack={() => {
                navigate("/payment/choose");
                return ok;
            }}
        />
    );

    return (
        <PaymentSetupPanel
            phase={phase}
            squareView={squareView}
            stripeView={stripeView}
            missingSecretView={missingSecretView}
        />
    );
};

export default PaymentSetupPage;
